import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  Copy,
  Crown,
  KeyRound,
  LogOut,
  Settings,
  Users,
  Zap,
} from 'lucide-react';
import { fundingAuthAPI } from '@/utils/api';
import { formatCreditAmount, setRuntimeModuleCredits } from '@/constants/ipeCredits';
import IpeConfirmDialog from './IpeConfirmDialog';
import UserAvatar from './UserAvatar';
import { buildRechargeUrl } from '@/config/ipeRoutes';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import {
  isMaxMembershipTier,
  resolveScopedPlanTier,
  resolveScopedQuota,
} from '@/pages/ipe/workbench/utils/accountCenterUtils';

function maskPhone(phone) {
  const p = String(phone || '').trim();
  if (p.length === 11) return `${p.slice(0, 3)} ${p.slice(3, 7).replace(/\d/g, '·')} ${p.slice(7)}`;
  return p;
}

function maskPhoneChip(phone) {
  const p = String(phone || '').trim();
  if (p.length === 11) return `${p.slice(0, 3)}...${p.slice(7)}`;
  return p;
}

function maskPhonePopover(phone) {
  const p = String(phone || '').trim();
  if (p.length === 11) return `${p.slice(0, 3)}****${p.slice(7)}`;
  return p;
}

function formatStorageCompact(mb) {
  const n = Number(mb) || 0;
  if (n <= 0) return '0G';
  if (n >= 1024) {
    const gb = n / 1024;
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)}G`;
  }
  return `${n}M`;
}

function resolveUserDisplayId(user) {
  if (!user) return '—';
  if (user.userNo) return String(user.userNo);
  return '—';
}

async function copyText(text, showToast, messages) {
  const value = String(text || '').trim();
  if (!value || value === '—') return;
  try {
    await navigator.clipboard.writeText(value);
    showToast?.(messages.copied, 'success');
  } catch {
    showToast?.(messages.failed, 'error');
  }
}

export default function IpeWorkbenchUserProfile({
  user,
  onLogout,
  onOpenAccountCenter,
  onOpenTeamManagement,
  onOpenOrderCenter,
  onOpenOpenKeys,
  showToast,
  variant = 'default',
}) {
  const { t } = useTranslation();
  const isChip = variant === 'chip';
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [accountSnapshot, setAccountSnapshot] = useState(null);
  const workspaceMode = useIpeWorkspaceMode();
  const rootRef = useRef(null);
  const closeTimerRef = useRef(null);

  const nickname = (user?.nickname && String(user.nickname).trim()) || '';
  const displayName = nickname || user?.phone || t('userFallback');
  const scopedQuota = resolveScopedQuota(accountSnapshot, workspaceMode);
  const quota = Object.keys(scopedQuota).length ? scopedQuota : (accountSnapshot?.quota || user?.quota || {});
  const storageLimitMb = Number(quota.storageLimitMb) || 0;
  const storageUsedMb = Number(quota.storageUsedMb ?? quota.usedStorageMb) || 0;
  const creditBalance = Number(quota.creditBalance ?? quota.usageRemaining) || 0;
  const storagePercent = storageLimitMb > 0
    ? Math.min(100, Math.round((storageUsedMb / storageLimitMb) * 100))
    : 0;
  const userDisplayId = resolveUserDisplayId(accountSnapshot?.user || user);
  const atMaxMembershipTier = isMaxMembershipTier(accountSnapshot, workspaceMode);
  const scopedPlanLabel = resolveScopedPlanTier(
    accountSnapshot?.user || user,
    scopedQuota,
    workspaceMode,
  );

  const openMenu = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setMenuOpen(true);
  };

  const scheduleClose = () => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => setMenuOpen(false), 180);
  };

  const closeMenu = () => setMenuOpen(false);

  const openAccountCenter = () => {
    closeMenu();
    if (onOpenAccountCenter) {
      onOpenAccountCenter();
      return;
    }
    window.dispatchEvent(new CustomEvent('ipe:open-account-center'));
  };

  const openTeamManagement = () => {
    closeMenu();
    if (onOpenTeamManagement) {
      onOpenTeamManagement();
      return;
    }
    window.dispatchEvent(new CustomEvent('ipe:open-team-management'));
  };

  const openOrderCenter = () => {
    closeMenu();
    if (onOpenOrderCenter) {
      onOpenOrderCenter();
      return;
    }
    window.dispatchEvent(new CustomEvent('ipe:open-order-center'));
  };

  const openOpenKeys = () => {
    closeMenu();
    if (onOpenOpenKeys) {
      onOpenOpenKeys();
      return;
    }
    window.dispatchEvent(new CustomEvent('ipe:open-open-keys'));
  };

  const openPurchasePage = useCallback((tab = 'membership', packGroup = null, packScope = null) => {
    closeMenu();
    navigate(buildRechargeUrl({
      tab: tab === 'packs' ? 'packs' : 'membership',
      packGroup: tab === 'packs' ? packGroup : undefined,
      packScope: packScope === 'team' || packScope === 'personal' ? packScope : workspaceMode,
    }));
  }, [navigate, workspaceMode]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const refreshAccountSnapshot = useCallback(async () => {
    try {
      const data = await fundingAuthAPI.getAccountCenter();
      setAccountSnapshot(data);
      if (data?.moduleCredits?.length) {
        setRuntimeModuleCredits(data.moduleCredits);
      }
    } catch {
      setAccountSnapshot(null);
    }
  }, []);

  useEffect(() => {
    if (!isChip && !menuOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await fundingAuthAPI.getAccountCenter();
        if (!cancelled) {
          setAccountSnapshot(data);
          if (data?.moduleCredits?.length) {
            setRuntimeModuleCredits(data.moduleCredits);
          }
        }
      } catch {
        if (!cancelled) setAccountSnapshot(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isChip, menuOpen, user?.id, workspaceMode]);

  useEffect(() => {
    if (!isChip) return undefined;
    const onWorkspaceRefresh = () => {
      refreshAccountSnapshot();
    };
    // mode-changed 已由上方 workspaceMode effect 覆盖
    window.addEventListener('ipe:workspace-refresh', onWorkspaceRefresh);
    return () => {
      window.removeEventListener('ipe:workspace-refresh', onWorkspaceRefresh);
    };
  }, [isChip, refreshAccountSnapshot]);

  const phoneLine = user?.phone
    ? isChip
      ? maskPhoneChip(user.phone)
      : maskPhone(user.phone)
    : '';

  return (
    <div
      ref={rootRef}
      className={`ipe-workbench-profile${isChip ? ' ipe-workbench-profile--chip' : ''}`}
    >
      <div className={`ipe-workbench-profile__header-row${isChip ? ' ipe-workbench-profile__header-row--chip' : ''}`}>
        {isChip ? (
          <div
            className="ipe-workbench-profile__credit-bar"
            onMouseEnter={() => {
              if (closeTimerRef.current) {
                window.clearTimeout(closeTimerRef.current);
                closeTimerRef.current = null;
              }
              setMenuOpen(false);
            }}
          >
            <div className="ipe-workbench-profile__credit-balance">
              <span className="ipe-workbench-profile__credit-icon" aria-hidden="true">
                <Zap size={11} strokeWidth={2.6} />
              </span>
              <span className="ipe-workbench-profile__credit-value">{formatCreditAmount(creditBalance)}</span>
            </div>
            <button
              type="button"
              className="ipe-workbench-profile__credit-recharge"
              onClick={() => openPurchasePage('packs')}
            >
              {t('recharge')}
            </button>
            <span className="ipe-workbench-profile__credit-divider" aria-hidden="true" />
            {atMaxMembershipTier ? (
              <span
                className="ipe-workbench-profile__credit-upgrade ipe-workbench-profile__credit-upgrade--max"
                aria-label={t('atMaxTierLabel', { plan: scopedPlanLabel })}
              >
                <Crown size={13} strokeWidth={2} aria-hidden="true" />
                {t('atMaxTier')}
              </span>
            ) : (
              <button
                type="button"
                className="ipe-workbench-profile__credit-upgrade"
                onClick={() => openPurchasePage('membership')}
              >
                <Crown size={13} strokeWidth={2} aria-hidden="true" />
                {t('upgradeMembership')}
              </button>
            )}
          </div>
        ) : null}

        <button
          type="button"
          className={`ipe-workbench-profile__trigger${isChip ? ' ipe-workbench-profile__trigger--chip' : ''}`}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          onMouseEnter={openMenu}
          onMouseLeave={scheduleClose}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <UserAvatar
            avatarUrl={user?.avatarUrl || accountSnapshot?.user?.avatarUrl}
            displayName={displayName}
            className="ipe-workbench-profile__avatar"
            size={isChip ? 32 : 36}
          />
          {!isChip ? (
            <span className="ipe-workbench-profile__text">
              <span className="ipe-workbench-profile__name">{displayName}</span>
              {phoneLine ? <span className="ipe-workbench-profile__sub">{phoneLine}</span> : null}
            </span>
          ) : null}
        </button>
      </div>

      {menuOpen ? (
        <div
          className="ipe-workbench-profile__popover"
          role="menu"
          onMouseEnter={openMenu}
          onMouseLeave={scheduleClose}
        >
          <div className="ipe-workbench-profile__popover-head">
            <UserAvatar
              avatarUrl={user?.avatarUrl || accountSnapshot?.user?.avatarUrl}
              displayName={displayName}
              className="ipe-workbench-profile__popover-avatar"
              size={44}
            />
            <div className="ipe-workbench-profile__popover-identity">
              <div className="ipe-workbench-profile__popover-phone-title">
                {user?.phone ? maskPhonePopover(user.phone) : t('noPhone')}
              </div>
              <div className="ipe-workbench-profile__popover-id-row">
                <span className="ipe-workbench-profile__popover-id">{t('userId', { id: userDisplayId })}</span>
                <button
                  type="button"
                  className="ipe-workbench-profile__popover-copy"
                  aria-label={t('copyUserId')}
                  title={t('copyUserId')}
                  onClick={() => copyText(userDisplayId, showToast, { copied: t('copied'), failed: t('copyFailed') })}
                >
                  <Copy size={13} strokeWidth={1.8} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          <div className="ipe-workbench-profile__stats-panel">
            <div className="ipe-workbench-profile__stat-card">
              <div className="ipe-workbench-profile__stat-title">{t('storage')}</div>
              <div
                className="ipe-workbench-profile__stat-progress"
                role="progressbar"
                aria-valuenow={storagePercent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('storageUsage')}
              >
                <span
                  className="ipe-workbench-profile__stat-progress-bar"
                  style={{ width: `${storagePercent}%` }}
                />
              </div>
              <div className="ipe-workbench-profile__stat-foot">
                <span className="ipe-workbench-profile__stat-value">
                  {formatStorageCompact(storageUsedMb)}/{formatStorageCompact(storageLimitMb)}
                </span>
                <button
                  type="button"
                  className="ipe-workbench-profile__stat-link"
                  onClick={() => openPurchasePage('packs', 'storage')}
                >
                  {t('upgrade')}
                </button>
              </div>
            </div>

            <div className="ipe-workbench-profile__stat-card">
              <div className="ipe-workbench-profile__stat-title">{t('ipeCredits')}</div>
              <div className="ipe-workbench-profile__stat-points">
                <span className="ipe-workbench-profile__stat-points-value">
                  {formatCreditAmount(creditBalance)}
                </span>
                <span className="ipe-workbench-profile__stat-points-icon" aria-hidden="true">
                  <Zap size={11} strokeWidth={2.4} />
                </span>
              </div>
              <div className="ipe-workbench-profile__stat-foot">
                <span className="ipe-workbench-profile__stat-spacer" aria-hidden="true" />
                <button
                  type="button"
                  className="ipe-workbench-profile__stat-link"
                  onClick={() => openPurchasePage('packs', 'credit')}
                >
                  {t('buy')}
                </button>
              </div>
            </div>
          </div>

          <div className="ipe-workbench-profile__menu-list">
            <button
              type="button"
              className="ipe-workbench-profile__menu-item"
              role="menuitem"
              onClick={openAccountCenter}
            >
              <Settings size={16} strokeWidth={1.8} aria-hidden="true" />
              {t('accountSettings')}
            </button>
            <button
              type="button"
              className="ipe-workbench-profile__menu-item"
              role="menuitem"
              onClick={openTeamManagement}
            >
              <Users size={16} strokeWidth={1.8} aria-hidden="true" />
              {t('teamManagement')}
            </button>
            <button
              type="button"
              className="ipe-workbench-profile__menu-item"
              role="menuitem"
              onClick={openOrderCenter}
            >
              <ClipboardList size={16} strokeWidth={1.8} aria-hidden="true" />
              {t('billingCenter')}
            </button>
            <button
              type="button"
              className="ipe-workbench-profile__menu-item"
              role="menuitem"
              onClick={openOpenKeys}
            >
              <KeyRound size={16} strokeWidth={1.8} aria-hidden="true" />
              {t('openIntegration')}
            </button>
            <button
              type="button"
              className="ipe-workbench-profile__menu-item ipe-workbench-profile__menu-item--danger"
              role="menuitem"
              onClick={() => {
                closeMenu();
                setLogoutConfirmOpen(true);
              }}
            >
              <LogOut size={16} strokeWidth={1.8} aria-hidden="true" />
              {t('logout')}
            </button>
          </div>
        </div>
      ) : null}

      <IpeConfirmDialog
        open={logoutConfirmOpen}
        title={t('logout')}
        message={t('logoutMessage')}
        confirmLabel={t('logout')}
        cancelLabel={t('cancel')}
        confirmDanger
        onCancel={() => setLogoutConfirmOpen(false)}
        onConfirm={() => {
          setLogoutConfirmOpen(false);
          onLogout?.();
        }}
      />
    </div>
  );
}
